import { useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useDealCheckStore } from '@/stores/dealCheckStore';
import { calculateDealCheck } from '@/lib/calculations';
import { DealAmpel } from './DealAmpel';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { formatCurrency, centsToEuros, eurosToCents } from '@/lib/utils';
import type { DealCheck, DealCheckFormData } from '@/types';
import { toast } from 'sonner';

interface DealCheckPanelProps {
  propertyId: string;
}

export function DealCheckPanel({ propertyId }: DealCheckPanelProps) {
  const { dealCheck, isLoading, fetchDealCheck, saveDealCheck } = useDealCheckStore();

  const { register, control, handleSubmit, reset } = useForm<DealCheckFormData>({
    defaultValues: { exit_confidence: 'medium' },
  });

  useEffect(() => {
    fetchDealCheck(propertyId);
  }, [propertyId, fetchDealCheck]);

  useEffect(() => {
    if (dealCheck) {
      reset({
        estimated_exit_value: centsToEuros(dealCheck.estimated_exit_value) ?? undefined,
        exit_value_reasoning: dealCheck.exit_value_reasoning ?? '',
        exit_confidence: dealCheck.exit_confidence,
        asking_price: centsToEuros(dealCheck.asking_price) ?? undefined,
        cost_transfer_tax: centsToEuros(dealCheck.cost_transfer_tax) ?? undefined,
        cost_notary: centsToEuros(dealCheck.cost_notary) ?? undefined,
        cost_land_registry: centsToEuros(dealCheck.cost_land_registry) ?? undefined,
        cost_broker: centsToEuros(dealCheck.cost_broker) ?? undefined,
        cost_other: centsToEuros(dealCheck.cost_other) ?? undefined,
        estimated_renovation: centsToEuros(dealCheck.estimated_renovation) ?? undefined,
        pros: dealCheck.pros ?? '',
        cons: dealCheck.cons ?? '',
        open_questions: dealCheck.open_questions ?? '',
      });
    }
  }, [dealCheck, reset]);

  const watchedValues = useWatch({ control });

  const result = useMemo(() => {
    const mockCheck: DealCheck = {
      id: '', property_id: propertyId, created_at: '', updated_at: '',
      estimated_exit_value: eurosToCents(watchedValues.estimated_exit_value ?? null),
      exit_value_reasoning: null, exit_confidence: 'medium',
      asking_price: eurosToCents(watchedValues.asking_price ?? null),
      cost_transfer_tax: eurosToCents(watchedValues.cost_transfer_tax ?? null),
      cost_notary: eurosToCents(watchedValues.cost_notary ?? null),
      cost_land_registry: eurosToCents(watchedValues.cost_land_registry ?? null),
      cost_broker: eurosToCents(watchedValues.cost_broker ?? null),
      cost_other: eurosToCents(watchedValues.cost_other ?? null),
      estimated_renovation: eurosToCents(watchedValues.estimated_renovation ?? null),
      pros: null, cons: null, open_questions: null,
    };
    return calculateDealCheck(mockCheck);
  }, [watchedValues, propertyId]);

  const onSubmit = async (data: DealCheckFormData) => {
    try {
      await saveDealCheck(propertyId, data);
      toast.success('Deal-Check gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      {/* Ampel & Ergebnis */}
      <div className="lg:col-span-1 space-y-4">
        {result ? (
          <>
            <DealAmpel status={result.dealStatus} percent={result.allInPercent} />
            <Card className="p-4 space-y-3">
              <div className="flex justify-between">
                <span className="text-foreground-muted">All-in Summe</span>
                <span className="font-semibold">{formatCurrency(result.allInTotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-foreground-muted">Pot. Gewinn</span>
                <span className={`font-semibold ${result.potentialProfit >= 0 ? 'text-positive' : 'text-negative'}`}>
                  {formatCurrency(result.potentialProfit)}
                </span>
              </div>
              <div className="border-t border-border pt-3">
                <p className="text-xs text-foreground-muted mb-2 uppercase tracking-wider">Max. Kaufpreis für:</p>
                <div className="flex justify-between text-sm">
                  <span>70% Regel</span>
                  <span className="text-deal-go font-medium">{formatCurrency(result.maxPurchase70)}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span>75% Regel</span>
                  <span className="text-deal-maybe font-medium">{formatCurrency(result.maxPurchase75)}</span>
                </div>
              </div>
            </Card>
          </>
        ) : (
          <Card className="p-6 text-center text-foreground-muted">
            Gib einen Exit-Wert ein, um die Deal-Bewertung zu sehen.
          </Card>
        )}
      </div>

      {/* Formular */}
      <form onSubmit={handleSubmit(onSubmit)} className="lg:col-span-2 space-y-6">
        {/* Exit-Wert */}
        <Card className="p-5">
          <h3 className="text-heading mb-4">Exit-Einschätzung</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Geschätzter Exit-Wert (€)</Label>
              <Input type="number" step="1000" {...register('estimated_exit_value', { valueAsNumber: true })} />
            </div>
            <div className="space-y-2">
              <Label>Konfidenz</Label>
              <select 
                {...register('exit_confidence')}
                className="flex h-10 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
              >
                <option value="high">Sicher</option>
                <option value="medium">Mittel</option>
                <option value="low">Unsicher</option>
              </select>
            </div>
          </div>
          <div className="mt-4 space-y-2">
            <Label>Begründung / Vergleichsobjekte</Label>
            <textarea 
              {...register('exit_value_reasoning')}
              className="flex min-h-[80px] w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring resize-none"
            />
          </div>
        </Card>

        {/* Kosten */}
        <Card className="p-5">
          <h3 className="text-heading mb-4">All-in Kalkulation</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Angebotspreis (€)</Label>
              <Input type="number" step="1000" {...register('asking_price', { valueAsNumber: true })} />
            </div>
            <div className="space-y-2">
              <Label>Sanierungskosten (€)</Label>
              <Input type="number" step="1000" {...register('estimated_renovation', { valueAsNumber: true })} />
            </div>
          </div>
          <p className="text-xs text-foreground-muted mt-6 mb-3 uppercase tracking-wider">Kaufnebenkosten:</p>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-2">
              <Label className="text-xs">Grunderwerbsteuer</Label>
              <Input type="number" step="100" {...register('cost_transfer_tax', { valueAsNumber: true })} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Notar</Label>
              <Input type="number" step="100" {...register('cost_notary', { valueAsNumber: true })} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Grundbuch</Label>
              <Input type="number" step="100" {...register('cost_land_registry', { valueAsNumber: true })} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Makler</Label>
              <Input type="number" step="100" {...register('cost_broker', { valueAsNumber: true })} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Sonstiges</Label>
              <Input type="number" step="100" {...register('cost_other', { valueAsNumber: true })} />
            </div>
          </div>
        </Card>

        {/* Notizen */}
        <Card className="p-5">
          <h3 className="text-heading mb-4">Einschätzung</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Was spricht dafür?</Label>
              <textarea 
                {...register('pros')}
                className="flex min-h-[100px] w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring resize-none"
              />
            </div>
            <div className="space-y-2">
              <Label>Was spricht dagegen?</Label>
              <textarea 
                {...register('cons')}
                className="flex min-h-[100px] w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring resize-none"
              />
            </div>
          </div>
          <div className="mt-4 space-y-2">
            <Label>Offene Fragen / To-Dos</Label>
            <textarea 
              {...register('open_questions')}
              className="flex min-h-[80px] w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring resize-none"
            />
          </div>
        </Card>

        <div className="flex justify-end">
          <Button type="submit" isLoading={isLoading}>Speichern</Button>
        </div>
      </form>
    </div>
  );
}
